import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { RA10OptionLetter } from '@/config/instruments';
import { RA_10_ITEMS } from '@/config/instruments';

export interface RA10RunnerProps {
  commercialName: string;
  onComplete: (choices: RA10OptionLetter[]) => void;
  initialChoices?: RA10OptionLetter[];
}

const OPTIONS: RA10OptionLetter[] = ['A', 'B', 'C'];

export function RA10Runner({
  commercialName,
  onComplete,
  initialChoices = [],
}: RA10RunnerProps) {
  const pad10 = (arr: RA10OptionLetter[]) =>
    [...arr, ...(Array(10 - arr.length).fill(undefined) as (RA10OptionLetter | undefined)[])].slice(0, 10);
  const [choices, setChoices] = useState<(RA10OptionLetter | undefined)[]>(() => pad10(initialChoices));
  const [currentIndex, setCurrentIndex] = useState(0);

  const setChoice = useCallback((idx: number, letter: RA10OptionLetter) => {
    setChoices((prev) => {
      const next = [...prev];
      next[idx] = letter;
      return next;
    });
  }, []);

  const item = RA_10_ITEMS[currentIndex];
  const choice = choices[currentIndex];

  const allComplete = RA_10_ITEMS.every((_, i) => choices[i] != null);

  const handleNext = () => {
    if (currentIndex < RA_10_ITEMS.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (allComplete) {
      onComplete(choices.filter((x): x is RA10OptionLetter => x != null) as RA10OptionLetter[]);
    }
  };

  const handleSubmit = () => {
    if (!allComplete) return;
    onComplete(choices.filter((x): x is RA10OptionLetter => x != null) as RA10OptionLetter[]);
  };

  if (!item) return null;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-lg">{commercialName}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Pregunta {currentIndex + 1} de {RA_10_ITEMS.length}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {currentIndex === 0 && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            Instrucciones: Lee cada situación y elige la opción que más se parezca a cómo sueles actuar en el trabajo. No hay respuestas correctas o incorrectas. Responde con honestidad.
          </p>
        )}
        <p className="font-medium">{item.stem}</p>
        <div className="flex flex-col gap-2">
          {item.options.map((opt) => (
            <label
              key={opt.letter}
              className="flex cursor-pointer items-center gap-2 rounded-md border p-3 hover:bg-muted/50"
            >
              <input
                type="radio"
                name={item.id}
                checked={choice === opt.letter}
                onChange={() => setChoice(currentIndex, opt.letter)}
              />
              <span className="text-sm">
                <strong>{opt.letter})</strong> {opt.text}
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((i) => i - 1)}
          >
            Anterior
          </Button>
          {currentIndex < RA_10_ITEMS.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!choice}
            >
              Siguiente
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={!allComplete}>
              Finalizar prueba
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Respondidas: {choices.filter((c) => c != null).length} / {RA_10_ITEMS.length}
        </p>
      </CardContent>
    </Card>
  );
}
